import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Paper,
  Divider,
} from "@mui/material";
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { whiteLogo } from "../../assets";
import { Link, useNavigate } from "react-router-dom";
import { notification } from "antd";
import { Logs } from "../../services";
import { sendOtpApi, verifyOtpApi, sendLoginLinkApi, verifyMagicLinkApi } from "../../services/authService";
import { loginUser } from "../../store/reducers/auth";
import { useDispatch } from "react-redux";

const primaryBlue = "#0e3584";
const OTP_SLOTS = ["first", "second", "third", "fourth", "fifth", "sixth"];

export const OtpLogin = ({ handleBackLogin }) => {
  const [emailOrMobile, setEmailOrMobile] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resendTimer, setResendTimer] = useState(0);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const token = queryParams.get("token");

    if (token) {
      verifyMagicLogin(token);
    }
  }, []);

  const verifyMagicLogin = async (token) => {
    setIsLoading(true);
    try {
      const res = await verifyMagicLinkApi(token);
      if (res.status === 200) {
        dispatch(loginUser(res.data));
        notification.success({
          message: "Login Successful",
          description: "Magic link verified successfully.",
        });
        if (res.data?.user?.role?.toLowerCase() === "vkyc auditor") {
          navigate("/product-catalogue", { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      }
    } catch (error) {
      notification.error({
        message: "Magic Link Invalid or Expired",
        description: error?.response?.data?.error?.message || "Please login again using OTP or Email link.",
      });
      Logs(error);
    } finally {
      setIsLoading(false);
    }
  };

  const startResendTimer = () => {
    setResendTimer(60);
  };

  useEffect(() => {
    if (resendTimer === 0) return;

    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleEmailChange = (e) => {
    const value = e.target.value.trim();
    setEmailOrMobile(value);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (value && !emailRegex.test(value)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      const next = document.getElementById(`otp-${index + 1}`);
      if (next) next.focus();
    }
  };

  const handleMagicLinkLogin = async () => {
    if (!emailOrMobile) {
      setEmailError("Email is required");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailOrMobile)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    setIsLoading(true);
    setEmailError("");
    try {
      const email = emailOrMobile.toLowerCase();
      const res = await sendLoginLinkApi(email);
      if (res.status === 200) {
        notification.success({
          message: "Login Link Sent!",
          description: "Check your email — a secure login link has been sent.",
        });
      }
    } catch (error) {
      notification.error({
        message: "Magic Link Failed!",
        description: error?.response?.data?.error?.message || "Unable to send magic link. Try again.",
      });
      Logs(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpLogin = async () => {
    if (!emailOrMobile) {
      setEmailError("Email is required");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailOrMobile)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    setIsLoading(true);
    setEmailError("");
    try {
      const email = emailOrMobile.toLowerCase();
      const res = await sendOtpApi(email);
      if (res.status === 200) {
        notification.success({
          message: "OTP Sent Successfully!",
          description: "A verification OTP has been sent. Please check your inbox.",
        });
        startResendTimer();
        setShowOtpScreen(true);
      }
    } catch (error) {
      notification.error({
        message: "Failed to Send OTP",
        description: error?.response?.data?.error?.message || "Something went wrong. Please try again!",
      });
      Logs(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const otpValue = otp.join("");
    if (otpValue.length < 6) {
      notification.warning({
        message: "Incomplete OTP",
        description: "Please enter the complete 6-digit OTP.",
      });
      return;
    }
    setIsLoading(true);
    try {
      const email = emailOrMobile.toLowerCase();
      const res = await verifyOtpApi(email, otpValue);
      if (res.status === 200) {
        dispatch(loginUser(res.data));
        if (res.data?.user?.role?.toLowerCase() === "vkyc auditor") {
          navigate("/product-catalogue", { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      }
    } catch (error) {
      notification.error({
        message: "OTP Verification Failed",
        description: error?.response?.data?.error?.message || "Something went wrong. Please try again!",
      });
      Logs(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!emailOrMobile) {
      notification.warning({
        message: "Email Required",
        description: "Please enter your email before requesting OTP.",
      });
      return;
    }
    setIsLoading(true);
    try {
      const email = emailOrMobile.toLowerCase();
      const res = await sendOtpApi(email);
      if (res.status === 200) {
        notification.success({
          message: "OTP Resent",
          description: "A new OTP has been sent to your email.",
        });
        startResendTimer();
      }
    } catch (error) {
      notification.error({
        message: "Failed to Resend OTP",
        description: error?.response?.data?.error?.message || "Something went wrong. Please try again!",
      });
      Logs(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        bgcolor: primaryBlue,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Left panel */}
      <Box
        sx={{
          width: { xs: "0", md: "50%" },
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "center",
          pl: { md: 12, lg: 20 },
          color: "#fff",
          height: "100vh",
          bgcolor: primaryBlue,
        }}
      >
        <Box component="img" src={whiteLogo} sx={{ width: 240, mb: 2 }} />
        <Typography sx={{ fontSize: 18, opacity: 0.9, mb: 4, color: "#fff" }}>
          It is a unified financial data and services platform that provides ready-to-use APIs across lending, identity, compliance, and other financial workflows, enabling businesses to quickly integrate, scale, and innovate without rebuilding core infrastructure
        </Typography>
        <Stack spacing={2}>
          <Typography sx={{ fontSize: 18, opacity: 0.9, color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
            • Compliance-first onboarding
          </Typography>
          <Typography sx={{ fontSize: 18, opacity: 0.9, color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
            • Faster fraud detection
          </Typography>
          <Typography sx={{ fontSize: 18, opacity: 0.9, color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
            • Access multiple APIs seamlessly
          </Typography>
          <Typography sx={{ fontSize: 18, opacity: 0.9, color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
            • Trusted by leading BFSIs
          </Typography>
        </Stack>
      </Box>

      {/* Right panel */}
      <Box
        sx={{
          width: { xs: "100%", sm: "90%", md: "50%", lg: "35%" },
          minHeight: { xs: "100vh", md: "auto" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "transparent",
        }}
      >
        <Paper
          elevation={4}
          sx={{
            borderRadius: "20px",
            px: { xs: 2, sm: 8 },
            py: { xs: 4, sm: 8 },
            width: "100%",
            maxWidth: 420,
            boxSizing: "border-box",
          }}
        >
          {!showOtpScreen ? (
            <>
              <Typography variant="h4" fontWeight={600} textAlign="center" mb={4}>
                Secure access to Finhub
              </Typography>

              <Stack spacing={3}>
                <Box>
                  <Typography sx={{ mb: 1, fontWeight: 500 }}>
                    Email Address
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter your email address"
                    value={emailOrMobile}
                    onChange={handleEmailChange}
                    variant="outlined"
                    size="medium"
                    error={Boolean(emailError)}
                    helperText={emailError}
                    FormHelperTextProps={{
                      sx: { fontSize: 10, mt: 0.5 },
                    }}
                    InputProps={{
                      style: { fontSize: 18, borderRadius: 8 },
                    }}
                  />
                </Box>

                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleMagicLinkLogin}
                  disabled={isLoading}
                  sx={{ fontSize: 18, fontWeight: 600, py: 1.5, mt: 1 }}
                >
                  Login via email link
                </Button>

                <Divider sx={{ my: 5, fontSize: "16px", fontWeight: 500 }}>or</Divider>

                <Button
                  variant="outlined"
                  onClick={handleOtpLogin}
                  disabled={isLoading}
                  sx={{
                    fontSize: 16,
                    fontWeight: 600,
                    py: 1.5,
                    borderWidth: 2,
                    "&:hover": { borderWidth: 2 },
                  }}
                >
                  Login via OTP
                </Button>

                <Button
                  sx={{ alignSelf: "center", textTransform: "none" }}
                  onClick={handleBackLogin}
                  size="small"
                >
                  Back to Sign In
                </Button>
              </Stack>

              <Typography mt={3} sx={{ textAlign: "center", fontSize: "16px" }}>
                Don&apos;t Have Account?{" "}
                <Link
                  to="https://www.habilelabs.io/contact-us"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#1976d2",
                    textDecoration: "none",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = "#1976d2";
                    e.target.style.textDecoration = "underline";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = "#1976d2";
                    e.target.style.textDecoration = "none";
                  }}
                >
                  Contact us
                </Link>
              </Typography>
              <Box mt={3} textAlign="center">
                <Typography variant="body2" color="text.secondary" fontSize={16}>
                  powered by{" "}
                  <a
                    href="https://www.habilelabs.io/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#2ab6e5",
                      fontWeight: 500,
                      textDecoration: "none",
                    }}
                  >
                    HabileLabs
                  </a>
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Typography textAlign="center" mb={3}>
                OTP has been sent to{" "}
                <strong style={{ color: primaryBlue }}>{emailOrMobile}</strong>
              </Typography>

              <Stack direction="row" justifyContent="center" spacing={1.5} mb={3}>
                {otp.map((digit, i) => (
                  <TextField
                    key={OTP_SLOTS[i]}
                    id={`otp-${i}`}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    inputProps={{
                      maxLength: 1,
                      style: {
                        textAlign: "center",
                        fontSize: "22px",
                        width: "45px",
                        height: "45px",
                      },
                    }}
                  />
                ))}
              </Stack>

              <Typography
                onClick={resendTimer === 0 ? handleResendOtp : undefined}
                sx={{
                  textAlign: "center",
                  fontSize: 14,
                  textDecoration: resendTimer === 0 ? "underline" : "none",
                  cursor: resendTimer === 0 ? "pointer" : "not-allowed",
                  mb: 2,
                  opacity: resendTimer === 0 ? 1 : 0.6,
                }}
              >
                {resendTimer === 0 ? "Resend OTP" : `Resend OTP in ${resendTimer}s`}
              </Typography>

              <Button
                variant="contained"
                fullWidth
                sx={{ py: 1.5, fontSize: 16, fontWeight: 600 }}
                onClick={handleVerifyOtp}
              >
                Login
              </Button>

              <Typography
                onClick={() => setShowOtpScreen(false)}
                sx={{
                  textAlign: "center",
                  mt: 2,
                  fontSize: 14,
                  color: primaryBlue,
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                or Login with Email
              </Typography>
            </>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

OtpLogin.propTypes = {
  handleBackLogin: PropTypes.func.isRequired,
};
